import { FC } from 'react';
import { TransactionDetailInfo } from './transactionDetail.types';
import { useTranslation } from 'react-i18next';
import {
  Box,
  formattedCurrency,
  VisaDirectPushTransaction,
  Card,
  withStyles,
  capitalizeFirstLetter,
  TransactionType,
} from 'vopay';
import { TransactionCard, Title, DetailsRows, TransactionStatus } from 'UI';
import { useClientData } from '@/providers/ClientData.provider';
import ApprovalHistoryDetailsCard from './Card/ApprovalHistoryDetailsCard';
import styles from '../Transactions.module.scss';
import useElinxDetails from './hooks/useElinxDetails';

const cn = withStyles(styles);

type VisaDirectPushTransactionInfoProps = {
  transaction: VisaDirectPushTransaction;
  isParent?: boolean;
  jwt: string;
  ELinxRequestID?: string;
  transactionDetail: TransactionDetailInfo;
};

const VisaDirectPushTransactionInfo: FC<VisaDirectPushTransactionInfoProps> = ({
  transaction,
  isParent = false,
  jwt,
  ELinxRequestID = '',
  transactionDetail,
}) => {
  const { failureReason, accountId, hasErrorInfo } = transactionDetail;

  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.VisaDirectPushTransactionInfo',
  });

  const { t: tGlobal } = useTranslation('translations');

  const {
    response: { JWT: mainJwt },
  } = useClientData();

  const isMainAccount = !jwt || jwt === mainJwt;

  const { elinxAdditionalId, invoiceButton } = useElinxDetails({ ELinxRequestID, jwt });

  const transactionDetails = [
    {
      label: t('TransactionDetails.transactionStatus', { defaultValue: 'Transaction Status' }),
      value: (
        <Box jc="fe">
          <TransactionStatus
            status={transaction.TransactionStatus}
            label={capitalizeFirstLetter(
              tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
                defaultValue: transaction.TransactionStatus,
              }),
            )}
          />
        </Box>
      ),
      visible: isParent,
    },
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: t('visaDirectPush'),
    },
    {
      label: t('TransactionDetails.transactionDateTime', { defaultValue: 'Transaction Date Time' }),
      value: transaction.TransactionDateTime,
    },
    {
      label: t('TransactionDetails.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified,
    },
    {
      label: t('TransactionDetails.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('TransactionDetails.holdAmount', { defaultValue: 'Hold Amount' }),
      value: transaction.HoldAmount ? `${transaction.Currency} ${formattedCurrency(transaction.HoldAmount)}` : '-',
    },
    {
      label: t('TransactionDetails.clientReferenceNumber', { defaultValue: 'Client Reference Number' }),
      value: transaction.ClientReferenceNumber || '-',
    },
    {
      label: t('TransactionDetails.invoice', { defaultValue: 'Invoice' }),
      value: invoiceButton,
      visible: !!invoiceButton,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: failureReason || '-',
      visible: isParent && failureReason !== '',
    },
  ];

  const cardDetails = [
    {
      label: t('CardDetails.name', { defaultValue: 'Name' }),
      value: transaction.FirstName + transaction.LastName,
    },
    {
      label: t('CardDetails.cardNumber', { defaultValue: 'Card Number' }),
      value: transaction.MaskedCardNumber || '-',
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified,
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: failureReason || '-',
      visible: !isParent,
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      {!isParent && (
        <TransactionCard
          receiverName={transaction.FirstName + ' ' + transaction.LastName}
          amount={transaction.Amount}
          currency={transaction.Currency}
          transactionId={transaction.TransactionID}
          type={'Visa Direct Push'}
          failureReason={failureReason}
          contactId={accountId}
          jwt={jwt}
          isMainAccount={isMainAccount}
          additionalId={elinxAdditionalId}
        />
      )}

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsTitle', { defaultValue: 'Transaction Details' })}
        </Title>

        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      <ApprovalHistoryDetailsCard
        transactionID={transaction.TransactionID}
        status={transaction.TransactionStatus}
        type={TransactionType.VISA_DIRECT_PUSH}
        jwt={jwt}
      />

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('cardDetailsTitle', { defaultValue: 'Card Details' })}
        </Title>

        <DetailsRows className={cn('rows')} details={cardDetails} />
      </Card>

      {hasErrorInfo && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoTitle', { defaultValue: 'Error Info' })}
          </Title>

          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default VisaDirectPushTransactionInfo;
